import { Button, Stack, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useFirestore from "hooks/useFirestore";
import {
  LEASE_ENDING_SOON_WINDOW_IN_DAYS,
  addDays,
  daysBetween,
  formatLeaseDay,
  israelDayOf,
  leaseEndDayForPeriod,
  matchLeasePeriod,
  planLeaseReminders
} from "utils/listings/lease";
import type { ChangeEvent } from "react";
import type { TFunction } from "react-i18next";

/**
 * The rental agreement of a listing that is being marked — or already is — rented
 * ("הושכר"): its first and last day. Rendered inside "סגירת עסקה" for a rent listing only.
 *
 * The end date is what the realtor is reminded about on WhatsApp, so the line under the
 * fields says which days those reminders fall on, computed by the same rule the server's
 * job applies (`planLeaseReminders`). It never promises a message that will not come: a
 * lease entered in its last month says so instead.
 *
 * Both dates are optional. The form holds them as `YYYY-MM-DD` strings, like every other
 * date field of this dialog; `createOrUpdateListing` turns them into Timestamps.
 */

/** What the block changes. Two dates can move at once, hence one patch and not two events. */
export interface LeaseDatesPatch {
  leaseStartDate?: string | null;
  leaseEndDate?: string | null;
}

interface LeaseTermSectionProps {
  loading: boolean;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  /** Where a period starts counting from when no start date was typed: the signing date. */
  fallbackStartDay?: string | null;
  onChange: (lease: LeaseDatesPatch) => void;
}

const LEASE_PERIODS_IN_MONTHS = [12, 24] as const;

export function LeaseTermSection({
  loading,
  leaseStartDate,
  leaseEndDate,
  fallbackStartDay,
  onChange
}: LeaseTermSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { userSettings } = useFirestore();

  const todayDay = israelDayOf(new Date());
  const chosenPeriod = matchLeasePeriod(leaseStartDate, leaseEndDate, LEASE_PERIODS_IN_MONTHS);
  const endsBeforeStart = !!leaseStartDate && !!leaseEndDate && leaseEndDate < leaseStartDate;

  const handleStartChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    const newStartDay = value || null;

    // A chosen period travels with its start: "a year from the 1st" stays a year.
    onChange({
      leaseStartDate: newStartDay,
      ...(newStartDay && chosenPeriod
        ? { leaseEndDate: leaseEndDayForPeriod(newStartDay, chosenPeriod) }
        : {})
    });
  };

  const handleEndChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void =>
    onChange({ leaseEndDate: value || null });

  const handlePeriodPick = (months: number) => (): void => {
    const startDay = leaseStartDate || fallbackStartDay || todayDay;

    onChange({ leaseStartDate: startDay, leaseEndDate: leaseEndDayForPeriod(startDay, months) });
  };

  // Renewal: the new term begins the day after the current one ends.
  const handleRenewForYear = (): void => {
    const newStartDay = addDays(leaseEndDate, 1);

    onChange({ leaseStartDate: newStartDay, leaseEndDate: leaseEndDayForPeriod(newStartDay, 12) });
  };

  const canRenew =
    !!leaseEndDate &&
    !endsBeforeStart &&
    daysBetween(todayDay, leaseEndDate) <= LEASE_ENDING_SOON_WINDOW_IN_DAYS;

  const wantsReminders = userSettings?.wantToReceiveAlertsOnMyListingLeaseEnding !== false;

  return (
    <Stack gap={2}>
      <Typography variant="h5">{t("listingsPage.lease.title")}</Typography>
      <Stack gap={2} direction={{ xs: "column", sm: "row" }}>
        <TextField
          fullWidth
          type="date"
          name="leaseStartDate"
          label={t("listingsPage.lease.startDate")}
          value={leaseStartDate ?? ""}
          disabled={loading}
          InputLabelProps={{ shrink: true }}
          onChange={handleStartChange}
        />
        <TextField
          fullWidth
          type="date"
          name="leaseEndDate"
          label={t("listingsPage.lease.endDate")}
          value={leaseEndDate ?? ""}
          disabled={loading}
          error={endsBeforeStart}
          helperText={endsBeforeStart ? t("listingsPage.lease.endBeforeStart") : undefined}
          InputLabelProps={{ shrink: true }}
          // The browser refuses the submit too — the server's `update_listing` has the same rule.
          inputProps={{ min: leaseStartDate ?? undefined }}
          onChange={handleEndChange}
        />
      </Stack>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t("listingsPage.lease.period")}
        </Typography>
        {LEASE_PERIODS_IN_MONTHS.map((months) => (
          <Button
            key={months}
            size="small"
            disabled={loading}
            variant={chosenPeriod === months ? "contained" : "outlined"}
            onClick={handlePeriodPick(months)}
          >
            {t("listingsPage.lease.periodMonths", { months })}
          </Button>
        ))}
        {canRenew && (
          <Button size="small" variant="text" disabled={loading} onClick={handleRenewForYear}>
            {t("listingsPage.lease.renewForYear")}
          </Button>
        )}
      </Stack>
      {!endsBeforeStart && (
        <Typography variant="body2" color="text.secondary">
          {getReminderHint(t, leaseEndDate, todayDay, wantsReminders)}
        </Typography>
      )}
    </Stack>
  );
}

/** The one line that says what the realtor will get on WhatsApp for these dates. */
function getReminderHint(
  t: TFunction,
  leaseEndDate: string | null,
  todayDay: string,
  wantsReminders: boolean
): string {
  if (!wantsReminders) return t("listingsPage.lease.reminderHintOptedOut");
  if (!leaseEndDate) return t("listingsPage.lease.reminderHintNoEndDate");

  const upcoming = planLeaseReminders(leaseEndDate, todayDay)
    .filter(({ when }) => when !== "passed")
    .map(({ when, day }) =>
      when === "onDay"
        ? t("listingsPage.lease.reminderOn", { date: formatLeaseDay(day) })
        : t("listingsPage.lease.reminderSoon")
    );

  if (upcoming.length === 2) {
    return t("listingsPage.lease.reminderHintTwo", { first: upcoming[0], second: upcoming[1] });
  }
  if (upcoming.length === 1) return t("listingsPage.lease.reminderHintOne", { when: upcoming[0] });

  return daysBetween(todayDay, leaseEndDate) < 0
    ? t("listingsPage.lease.reminderHintEnded")
    : t("listingsPage.lease.reminderHintTooLate");
}
